import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react'
import PropTypes from 'prop-types'

const OTPVariant = Object.freeze({
  default: 'default',
  small: 'small',
})

const OTPInput = forwardRef((props, ref) => {
  const { onChange, autoFocus, variant = OTPVariant.default, disabled = false, length = 6 } = props
  const [otp, setOtp] = useState(Array(length).fill(''))
  const inputRefs = useRef([])

  useImperativeHandle(ref, () => ({
    clear: () => {
      setOtp(Array(length).fill(''))
      inputRefs.current.forEach((input) => {
        if (input) {
          input.value = ''
        }
      })
    },
  }))

  const handleChange = (e, index) => {
    const value = e.target.value
    if (/[^0-9]/.test(value)) return // Only allow numeric values

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)

    if (onChange) {
      onChange(newOtp.join(''))
    }

    // Move focus to next input
    if (value && index < length - 1) {
      document.getElementById(`otp-input-${index + 1}`).focus()
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move focus to the previous input if it's empty
      const prevInput = document.getElementById(`otp-input-${index - 1}`)
      prevInput.focus()
      moveCursorToEnd(prevInput)
    }

    if (e.key === 'ArrowRight' && index < otp.length - 1) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`)
      nextInput.focus()
      moveCursorToEnd(nextInput)
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`)
      prevInput.focus()
      moveCursorToEnd(prevInput)
    }
  }

  // move cursor to the end of the input box
  const moveCursorToEnd = (element) => {
    const length = element.value.length
    element.setSelectionRange(length, length)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('Text') // Get the pasted text
    const otpArray = pastedData.split('').slice(0, length) // Take only the necessary characters
    const newOtp = [...otp]

    otpArray.forEach((char, index) => {
      if (index < length) {
        newOtp[index] = char
      }
    })

    setOtp(newOtp)

    if (onChange) {
      onChange(newOtp.join(''))
    }
  }

  useEffect(() => {
    if (autoFocus) {
      document.getElementById('otp-input-0').focus()
    }
  }, [autoFocus])

  return (
    <div className={`otp-container otp-container--${variant}`}>
      {otp.map((digit, index) => (
        <input
          key={index}
          id={`otp-input-${index}`}
          type="text"
          maxLength="1"
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          className={`otp-input input-cell input-cell--input form-control s-50 ${digit ? 'otp-input--with-value' : ''}`}
          disabled={disabled}
          autoComplete="off"
          ref={(el) => (inputRefs.current[index] = el)}
        />
      ))}
    </div>
  )
})

OTPInput.displayName = 'OTPInput'
OTPInput.defaultProps = {
  length: 6,
  onChange: null,
  variant: OTPVariant.default,
  autoFocus: false,
  disabled: false,
}
OTPInput.OTPVariant = OTPVariant

OTPInput.propTypes = {
  length: PropTypes.number,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(OTPVariant)),
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default OTPInput
